import { defineStore } from 'pinia';
import { vec3, mat3, mat4 } from 'gl-matrix';
import { vtkImageData } from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { Bounds, Vector3 } from '@kitware/vtk.js/types';

import { useIdStore } from '@/src/store/id';
import { defaultLPSDirections, getLPSDirections } from '../utils/lps';
import { removeFromArray } from '../utils';
import { StateFile, DatasetType } from '../io/state-file/schema';
import { serializeData } from '../io/state-file/utils';
import { useFileStore } from './datasets-files';
import { ImageMetadata } from '../types/image';
import { compareImageSpaces } from '../utils/imageSpace';

export const defaultImageMetadata = () => ({
  name: '(none)',
  orientation: mat3.create(),
  lpsOrientation: defaultLPSDirections(),
  spacing: vec3.fromValues(1, 1, 1),
  origin: vec3.create(),
  dimensions: vec3.fromValues(1, 1, 1),
  worldBounds: [0, 1, 0, 1, 0, 1] as Bounds,
  worldToIndex: mat4.create(),
  indexToWorld: mat4.create(),
  isDynamic: false,
  mainAxis: 0,
});

interface State {
  idList: string[]; // list of IDs
  dataIndex: Record<string, vtkImageData>; // ID -> VTK object
  metadata: Record<string, ImageMetadata>; // ID -> metadata
}

export const useImageStore = defineStore('images', {
  state: (): State => ({
    idList: [],
    dataIndex: Object.create(null),
    metadata: Object.create(null),
  }),
  actions: {
    addVTKImageData(name: string, imageData: vtkImageData, nbOfTemporalPositions?:number, useId?: string) {
      if (useId && useId in this.dataIndex) {
        throw new Error('ID already exists');
      }

      //// Convert LPS to RAS image
      //// (for 3D image rendering to be correclty positioned/oriented with 3D vtkpolyData from SOFA simulation)
      //
      // Origin
      const RASorigin = vec3.create();
      vec3.multiply(RASorigin, imageData.getOrigin(), [-1.0, -1.0, 1.0]);
      imageData.setOrigin(RASorigin as Vector3)
      //
      // Orientation
      const RASDirection = imageData.getDirection();  // Create an empty matrix to store the result
      mat3.transpose(RASDirection,RASDirection);
      mat3.scale(RASDirection, RASDirection, [-1.0, -1.0]);
      mat3.transpose(RASDirection,RASDirection);
      imageData.setDirection(RASDirection)

      const id = useId || useIdStore().nextId();

      this.idList.push(id);
      this.dataIndex[id] = imageData;

      this.metadata[id] = { ...defaultImageMetadata(), name };
      const nbTempPos = nbOfTemporalPositions ?? 1;
      this.updateData(id, imageData, nbTempPos);
      return id;
    },

    updateData(id: string, imageData: vtkImageData, nbOfTemporalPositions?:number) {
      if (id in this.metadata) {
        const metadata: ImageMetadata = {
          name: this.metadata[id].name,
          dimensions: imageData.getDimensions() as vec3,
          spacing: imageData.getSpacing() as vec3,
          origin: imageData.getOrigin() as vec3,
          orientation: imageData.getDirection(),
          lpsOrientation: getLPSDirections(imageData.getDirection()),
          worldBounds: imageData.getBounds(),
          worldToIndex: imageData.getWorldToIndex(),
          indexToWorld: imageData.getIndexToWorld(),
          isDynamic: ((nbOfTemporalPositions??0)>1)??false,
          mainAxis: imageData.getDimensions().findIndex(dim => dim === nbOfTemporalPositions),
        };
        this.metadata[id] = metadata;
        this.dataIndex[id] = imageData;
      }
      this.dataIndex[id] = imageData;
    },

    updateMetaData(id: string)
    {
      const imageData = this.dataIndex[id];
      const metaData = this.metadata[id];
      // metaData.dimensions = imageData.getDimensions() as vec3;
      // metaData.spacing = imageData.getSpacing() as vec3;
      // metaData.origin = imageData.getOrigin() as vec3;
      // metaData.orientation = imageData.getDirection();
      // metaData.lpsOrientation = getLPSDirections(imageData.getDirection());
      metaData.worldBounds = imageData.getBounds();
      metaData.worldToIndex = imageData.getWorldToIndex();
      metaData.indexToWorld = imageData.getIndexToWorld();
      // metaData.isDynamic = ((nbOfTemporalPositions??0)>1)??false;
    },

    deleteData(id: string) {
      if (id in this.dataIndex) {
        delete this.dataIndex[id];
        delete this.metadata[id];
        removeFromArray(this.idList, id);
      }
    },

    checkAllImagesSameSpace() {
      if (this.idList.length < 2) return false;

      const dataFirst = this.dataIndex[this.idList[0]];
      const allEqual = this.idList.slice(1).every((id) => {
        return compareImageSpaces(this.dataIndex[id], dataFirst);
      });

      return allEqual;
    },

    async serialize(stateFile: StateFile) {
      const fileStore = useFileStore();
      // We want to filter out volume images (which are generated and don't have
      // input files in fileStore with matching imageID.)
      const dataIDs = this.idList.filter((id) => id in fileStore.byDataID);

      await serializeData(stateFile, dataIDs, DatasetType.IMAGE);
    },
  },
});
